import { useQuery } from '@tanstack/react-query';
import { useUniversalNavContext } from '../../universal-nav/hooks/useUniversalNavContext';
import { AccountPlanV3, PlanType, PlanInterval } from '../../../src/types';

interface AccountPlanRaw {
  interval: PlanInterval;
  plan_type: PlanType;
  actions: number;
  metered_pricing_tiers: Array<Array<number>>;
}

const formatAccountsPlanData = (
  accountsPlanData: AccountPlanRaw
): AccountPlanV3 => {
  return {
    interval: accountsPlanData.interval,
    planType: accountsPlanData.plan_type,
    actions: accountsPlanData.actions,
    meteredPricingTiers: accountsPlanData.metered_pricing_tiers.map(
      ([numTasks, pricePerTask]) => ({
        numTasks,
        pricePerTask,
      })
    ),
  };
};

export const useQueryAccountPlan = (accountId?: number, planId?: number) => {
  const { apiHostname } = useUniversalNavContext();

  const result = useQuery({
    queryKey: ['accountPlan', accountId, planId],
    queryFn: async () => {
      if (!accountId || !planId)
        throw new Error('Missing either accountId or planId');

      const response = await fetch(
        `${apiHostname}/api/v3/accounts/${encodeURIComponent(
          accountId
        )}/plans/${encodeURIComponent(planId)}`,
        {
          credentials:
            process.env.NODE_ENV !== 'test' ? 'include' : 'same-origin',
        }
      );

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const accountsPlanJson = await response.json();
      return accountsPlanJson;
    },
    select: formatAccountsPlanData,
    enabled: !!accountId && !!planId,
  });

  return {
    ...result,
    // isInitialLoading returns false if the query is disabled.
    // isLoading returns true, which is undesired behavior.
    isLoading: result.isInitialLoading,
  } as typeof result;
};
